
import React from 'react';
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { formatCurrency, formatDate } from '@/components/borrowings/debt/utils';
import { ALMContract } from './types';
import { 
  Calendar,
  Info
} from 'lucide-react';

interface ContractDetailsOverviewProps {
  contract: ALMContract;
}

const ContractDetailsOverview: React.FC<ContractDetailsOverviewProps> = ({ contract }) => {
  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Principal Amount</p>
              <p className="text-lg font-medium">{formatCurrency(contract.principalAmount)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Current Balance</p>
              <p className="text-lg font-medium">{formatCurrency(contract.currentBalance)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Interest Rate</p>
              <p className="text-lg font-medium">{contract.interestRate}%</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Interest Type</p>
              <p className="text-lg font-medium">{contract.interestType}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Key Dates</CardTitle>
            </div>
            <CardDescription>Important timeline information</CardDescription>
            
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Sanction Date</span>
                <span className="text-sm">{formatDate(contract.sanctionDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">First Payment</span>
                <span className="text-sm">{formatDate(contract.firstPaymentDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium">Maturity Date</span>
                <span className="text-sm font-medium">{formatDate(contract.maturityDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Next Reset</span>
                <span className="text-sm">{contract.nextResetDate ? formatDate(contract.nextResetDate) : 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Info className="h-4 w-4 text-muted-foreground" />
              <CardTitle className="text-base">Additional Info</CardTitle>
            </div>
            <CardDescription>Contract specifications</CardDescription>
            
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Repayment Frequency</span>
                <span className="text-sm">{contract.repaymentFrequency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Reset Frequency</span>
                <span className="text-sm">{contract.resetFrequency || 'N/A'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Prepayment Option</span>
                <span className="text-sm">{contract.prepaymentOption ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Penalties</span>
                <span className="text-sm">{contract.penalties ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {contract.counterparty && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <CardTitle className="text-base">Counterparty Information</CardTitle>
            </div>
            
            <div className="mt-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="text-sm">{contract.counterparty.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Type</span>
                <span className="text-sm">{contract.counterparty.type}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Rating</span>
                <span className="text-sm">{contract.counterparty.rating || 'N/A'}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContractDetailsOverview;
